/* eslint-disable no-async-promise-executor */
import type { RefeshTokenStatus } from './types';
import type { Token, IUser } from '@/types/models';
import { isEmptyVal, isElectron } from '@/utils/appUtil';
import {
  AppAuthRefeshTokenKey,
  AppAuthTokenKey,
  ExpireCookieDays,
} from '@/consts';
import { addDateByDays } from '@/utils/addDateByDays';
import { canRefreshToken } from '@/utils/jwtUtil';
import { refreshTokenSingleFlight } from '@/boot/axios';
import { defineStore } from 'pinia';
import { Cookies } from 'quasar';
import { computed, ref } from 'vue';

export const useAuthenStore = defineStore('authenStore', () => {
  const auth = ref<IUser>();
  const refreshTokenTimeout = ref<any>();
  const refreshTokenTimeoutNo = ref(0);
  const sessionExpired = ref(false);
  const devMode = process.env.NODE_ENV == 'development';
  const tokenKey = computed(() => auth.value);
  const loginedAvatar = computed(() => auth.value?.avatar);
  const loginedDisplay = computed(() =>
    auth.value?.firstName + ' ' + auth.value?.lastName,
  );

  const refreshToken = async (
    ssrContext: any | undefined = undefined,
  ): Promise<RefeshTokenStatus> => {
    return new Promise(async (resolve /* reject */) => {
      const isServerMode = process.env.SERVER;
      if (devMode) {
        console.log('refreshToken SERVER mode ', isServerMode);
      }
      const ck: any =
        isServerMode && ssrContext ? Cookies.parseSSR(ssrContext) : Cookies;
      if (ck) {
        const authTokenKey = ck.get(AppAuthTokenKey);
        if (!isEmptyVal(authTokenKey)) {
          const refreshIt = await canRefreshToken(
            authTokenKey,
            devMode,
            'authenStore > refreshToken',
          );
          // const refreshIt = true;
          if (refreshIt) {
            const refreshRes = await refreshTokenProcess(ssrContext);
            if (refreshRes && refreshRes.status) {
              if (!isServerMode) {
                await startRefreshTokenTimer();
              }
            }
            resolve(refreshRes);
          }
        }
      }
      resolve({
        status: true,
        fourceLogout: false,
      });
    });
  };

  const refreshTokenProcess = async (
    ssrContext: any
  ): Promise<RefeshTokenStatus> => {
    try {
      // Shared single-flight: this proactive path and the axios 401 interceptor
      // dedupe onto ONE /refresh-token call so they never both replay the
      // single-use refresh token (issue #686). ssrContext lets the server-side
      // path read the refresh token from the SSR cookie jar.
      const tokenData = await refreshTokenSingleFlight(ssrContext);

      await setRefreshTokenCookie(ssrContext, tokenData);
      sessionExpired.value = false;
      return {
        status: true,
        token: tokenData.accessToken,
        fourceLogout: false
      };
    } catch (error: any) {
      const errResponse = error?.response;
      let fourceLogout = false;
      // 401 or 403 on the refresh call means the refresh token itself is
      // rejected → force logout. Other statuses (5xx, network, parse) are
      // transient and must NOT log the user out.
      if (errResponse && (errResponse.status === 401 || errResponse.status === 403)) {
        sessionExpired.value = true;
        fourceLogout = true;
      }
      return {
        status: false,
        fourceLogout
      };
    }
  };

  const setRefreshTokenCookie = async (
    ssrContext: any,
    responseData: Token,
  ) => {
    return new Promise(async (resolve /* reject */) => {
      const ck: any =
        process.env.SERVER && ssrContext
          ? Cookies.parseSSR(ssrContext)
          : Cookies;

      const isDevMode = process.env.NODE_ENV == 'development';
      // if (isServerMode) {
      //   ck.set(SucureDeviceIdAtt, response.data.refreshToken, {
      //     expires: addDateByDays(ExpireCookieDays),
      //     path: '/',
      //     domain: isDevMode ? undefined : AppDomain,
      //     secure: true,
      //     httpOnly: true,
      //     sameSite: 'None'
      //   });
      // }
      ck.set(AppAuthRefeshTokenKey, responseData.refreshToken, {
        expires: addDateByDays(ExpireCookieDays),
        path: '/',
        // domain: isDevMode ? undefined : AppDomain,
        secure: !isDevMode,
        sameSite: 'Strict',
      });

      // In Electron, also save to localStorage
      if (isElectron() && typeof window !== 'undefined') {
        localStorage.setItem(AppAuthRefeshTokenKey, responseData.refreshToken);
      }

      ck.set(AppAuthTokenKey, responseData.accessToken, {
        expires: addDateByDays(ExpireCookieDays),
        path: '/',
        // domain: isDevMode ? undefined : AppDomain,
        secure: !isDevMode,
        sameSite: 'Lax',
      });

      // In Electron, also save to localStorage
      if (isElectron() && typeof window !== 'undefined') {
        localStorage.setItem(AppAuthTokenKey, responseData.accessToken);
      }

      resolve(true);
    });
  };

  const startRefreshTokenTimer = (ssrContext: any | undefined = undefined) => {
    return new Promise((resove) => resove(true));
  };

  const stopAndRestartRefreshTokenTimer = async () => {
    stopRefreshTokenTimer();
    await refreshToken();
    return new Promise(async (resolve /* reject */) => {
      resolve(true);
    });
  };

  const stopRefreshTokenTimer = () => {
    if (refreshTokenTimeout.value) {
      clearTimeout(refreshTokenTimeout.value);
      refreshTokenTimeout.value = null;
    }
  };

  const setAuthen = (item: IUser) => {
    auth.value = item;
  };

  const logout = () => {
    auth.value = undefined;
  };

  const methods = {
    logout,
    setAuthen,
    stopRefreshTokenTimer,
    stopAndRestartRefreshTokenTimer,
    startRefreshTokenTimer,
    setRefreshTokenCookie,
    refreshTokenProcess,
    refreshToken,
  };

  return {
    auth,
    refreshTokenTimeoutNo,
    sessionExpired,
    tokenKey,
    loginedAvatar,
    loginedDisplay,
    ...methods,
  };
});
